import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import usePortal from 'react-useportal';
import { useStore } from '../../../../../hooks';
import {
  errorToast,
  isValidMail,
  showCustomToast,
  validateMailInputValue,
  validateNameInputValue,
  validateNumberInputValue,
} from '../../../../../utils';
import { Button } from 'components/ui';
import {
  Divider,
  DividerLine,
  DividerText,
  Fade,
  Footer,
  InputsWrapper,
  Line,
  ModalWrapper,
  Title,
  Wrapper,
} from './add-modal.styled';
import { InputBlock } from '../../../index';

type IParamsKey =
  | 'service_name'
  | 'inn'
  | 'ogrn'
  | 'last_name'
  | 'first_name'
  | 'patronymic_name'
  | 'position'
  | 'email'
  | 'phone';

type IParams = {
  service_name: string;
  inn: string;
  ogrn: string;
  last_name: string;
  first_name: string;
  patronymic_name: string;
  position: string;
  email: string;
  phone: string;
};

export const AddModal = observer(() => {
  const { createRequest } = useStore();
  const {
    loading,
    addNewThirdPartyModal,
    setAddNewThirdPartyModalProperties,
    createNewOtnerPerson,
    createNewOtherProvider,
    createNewOtherEmployee,
  } = createRequest;
  const { thirdPartyType, organizationId, setSelectedId } = createRequest.applicant;

  const { Portal } = usePortal();

  const [nameError, setNameError] = useState<boolean>(false);
  const [innError, setInnError] = useState<boolean>(false);
  const [ogrnError, setOgrnError] = useState<boolean>(false);
  const [firstError, setFirstError] = useState<boolean>(false);
  const [lastError, setLastError] = useState<boolean>(false);
  const [patronymicError, setPatronymicError] = useState<boolean>(false);
  const [positionError, setPositionError] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<boolean>(false);
  const [mailError, setMailError] = useState<boolean>(false);

  const [params, setParams] = useState<IParams>({
    service_name: '',
    inn: '',
    ogrn: '',
    last_name: '',
    first_name: '',
    patronymic_name: '',
    position: '',
    email: '',
    phone: '',
  });

  const checkValidPersonContact = (): boolean => {
    return (
      params.last_name.length >= 2 &&
      params.first_name.length >= 2 &&
      params.patronymic_name.length !== 1 &&
      params.phone.length === 10 &&
      (isValidMail(params.email) || !params.email)
    );
  };

  const isValidPersonContact: boolean = useMemo(
    () => checkValidPersonContact(),
    [
      params.last_name,
      params.first_name,
      params.patronymic_name,
      params.phone,
      params.email,
    ]
  );

  const checkValidOrganizationContact = (): boolean => {
    return (
      params.service_name.length >= 2 &&
      (params.inn.length === 0 || params.inn.length === 10) &&
      (params.ogrn.length === 0 || params.ogrn.length === 13)
    );
  };

  const isValidOrganizationContact: boolean = useMemo(
    () => checkValidOrganizationContact(),
    [params.service_name, params.inn, params.ogrn, params.email]
  );

  const checkValidThirdPartyContact = (): boolean => {
    return (
      isValidPersonContact &&
      ((thirdPartyType === 'other_employee' &&
        params.position.length >= 2 &&
        (!addNewThirdPartyModal.addOrganization ||
          isValidOrganizationContact)) ||
        thirdPartyType === 'other_person')
    );
  };

  const isValidThirdPartyContact: boolean = useMemo(
    () => checkValidThirdPartyContact(),
    [
      isValidPersonContact,
      isValidOrganizationContact,
      params.position,
      addNewThirdPartyModal.addOrganization,
    ]
  );

  const setErrors = (key: IParamsKey): void => {
    switch (key) {
      case 'service_name':
        setNameError(params.service_name.length < 2);
        break;
      case 'inn':
        setInnError(!!params.inn && params.inn.length < 10);
        break;
      case 'ogrn':
        setOgrnError(!!params.ogrn && params.ogrn.length < 13);
        break;
      case 'last_name':
        setLastError(params.last_name.length < 2);
        break;
      case 'first_name':
        setFirstError(params.first_name.length < 2);
        break;
      case 'patronymic_name':
        setPatronymicError(params.patronymic_name.length === 1);
        break;
      case 'position':
        setPositionError(params.position.length < 2);
        break;
      case 'email':
        !!params.email && setMailError(!isValidMail(params.email));
        break;
      case 'phone':
        setPhoneError(params.phone.length !== 10);
        break;
      default:
        break;
    }
  };

  const handleLettersInputChange = (key: IParamsKey, value: string): void => {
    const validLetterSymbols = validateNameInputValue(value);
    setParams({
      ...params,
      [key]: validLetterSymbols,
    });

    switch (key) {
      case 'service_name':
        validLetterSymbols.length >= 2 && setNameError(false);
        break;
      case 'first_name':
        validLetterSymbols.length >= 2 && setFirstError(false);
        break;
      case 'last_name':
        validLetterSymbols.length >= 2 && setLastError(false);
        break;
      case 'patronymic_name':
        validLetterSymbols.length !== 1 && setPatronymicError(false);
        break;
      case 'position':
        validLetterSymbols.length >= 2 && setPositionError(false);
        break;
      default:
        break;
    }
  };

  const handleNumbersInputChange = (key: IParamsKey, value: string): void => {
    const validNumberSymbols = validateNumberInputValue(value);
    setParams({
      ...params,
      [key]: validNumberSymbols,
    });

    switch (key) {
      case 'inn':
        (validNumberSymbols.length === 10 || !validNumberSymbols.length) &&
          setInnError(false);
        break;
      case 'ogrn':
        (validNumberSymbols.length === 13 || !validNumberSymbols.length) &&
          setOgrnError(false);
        break;
      default:
        break;
    }
  };

  const changeMailParam = (key: IParamsKey, value: string): void => {
    const validMailSymbols = validateMailInputValue(value);
    setParams({
      ...params,
      [key]: validMailSymbols,
    });
    (!validMailSymbols || isValidMail(validMailSymbols)) && setMailError(false);
  };

  const changePhoneParam = (key: IParamsKey, value: string): void => {
    setParams({
      ...params,
      [key]: value,
    });
    value.length === 10 && setPhoneError(false);
  };

  const createNewThirdPartyAccount = async (): Promise<void> => {
    const title: string =
      thirdPartyType === 'other_employee'
        ? addNewThirdPartyModal.addOrganization
          ? 'Создана новая организация'
          : 'Создан новый представитель организации'
        : 'Создано новое частное лицо';

    if (
      thirdPartyType === 'other_employee' &&
      addNewThirdPartyModal.addOrganization
    ) {
      try {
        const newProvider = await createNewOtherProvider(params);
        await createNewOtherEmployee(newProvider._id, params);
        setSelectedId('organizationId', newProvider._id);
        showCustomToast({
          title: title,
        });
        setAddNewThirdPartyModalProperties('show', false);
      } catch (error) {
        setAddNewThirdPartyModalProperties('show', false);
        errorToast('Не удалось создать новую организацию');
      }
    }

    if (
      thirdPartyType === 'other_employee' &&
      !addNewThirdPartyModal.addOrganization
    ) {
      createNewOtherEmployee(organizationId, params).then(() => {
        showCustomToast({
          title: title,
        });
        setAddNewThirdPartyModalProperties('show', false);
      });
    }

    if (thirdPartyType === 'other_person') {
      createNewOtnerPerson(params).then(() => {
        showCustomToast({
          title: title,
        });
        setAddNewThirdPartyModalProperties('show', false);
      });
    }
  };

  return (
    <Portal>
      <Wrapper>
        <Fade
          onClick={() => setAddNewThirdPartyModalProperties('show', false)}
        />
        <ModalWrapper>
          <Title>
            {thirdPartyType === 'other_employee'
              ? addNewThirdPartyModal.addOrganization
                ? 'Новая организация'
                : 'Новый представитель организации'
              : 'Новое частное лицо'}
          </Title>
          <InputsWrapper>
            {thirdPartyType === 'other_employee' &&
              addNewThirdPartyModal.addOrganization && (
                <>
                  <Line>
                    <InputBlock
                      placeholder={'Название'}
                      value={params.service_name}
                      onBlur={() => setErrors('service_name')}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleLettersInputChange(
                          'service_name',
                          e.target.value || ''
                        )
                      }
                      showError={nameError}
                      errorMessage={'Введите не менее 2 символов'}
                    />
                  </Line>
                  <Line>
                    <InputBlock
                      placeholder={'ИНН (необязательно)'}
                      value={params.inn}
                      onBlur={() => setErrors('inn')}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleNumbersInputChange('inn', e.target.value || '')
                      }
                      maxLength={10}
                      showError={innError}
                      errorMessage={'Введите 10 символов'}
                    />
                    <InputBlock
                      placeholder={'ОГРН (необязательно)'}
                      value={params.ogrn}
                      onBlur={() => setErrors('ogrn')}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleNumbersInputChange('ogrn', e.target.value || '')
                      }
                      maxLength={13}
                      showError={ogrnError}
                      errorMessage={'Введите 13 символов'}
                    />
                  </Line>
                  <DividerLine>
                    <Divider />
                    <DividerText>Представитель</DividerText>
                    <Divider />
                  </DividerLine>
                </>
              )}

            <Line isWrap={thirdPartyType === 'other_person'}>
              <InputBlock
                placeholder={'Фамилия'}
                value={params.last_name}
                onBlur={() => setErrors('last_name')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleLettersInputChange('last_name', e.target.value || '')
                }
                showError={lastError}
                errorMessage={'Введите не менее 2 символов'}
              />
              <InputBlock
                placeholder={'Имя'}
                value={params.first_name}
                onBlur={() => setErrors('first_name')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleLettersInputChange('first_name', e.target.value || '')
                }
                showError={firstError}
                errorMessage={'Введите не менее 2 символов'}
              />
              <InputBlock
                placeholder={'Отчество (необязательно)'}
                value={params.patronymic_name}
                onBlur={() => setErrors('patronymic_name')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleLettersInputChange(
                    'patronymic_name',
                    e.target.value || ''
                  )
                }
                showError={patronymicError}
                errorMessage={'Введите не менее 2 символов'}
              />
            </Line>

            {thirdPartyType === 'other_employee' && (
              <Line>
                <InputBlock
                  placeholder={'Должность'}
                  value={params.position}
                  onBlur={() => setErrors('position')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleLettersInputChange('position', e.target.value || '')
                  }
                  showError={positionError}
                  errorMessage={'Введите не менее 2 символов'}
                />
              </Line>
            )}
            <Line>
              <InputBlock
                placeholder={'Email (необязательно)'}
                value={params.email}
                onBlur={() => setErrors('email')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  changeMailParam('email', e.target.value || '')
                }
                showError={mailError}
                errorMessage={'Некорректный email'}
              />
            </Line>

            <Line>
              <InputBlock
                placeholder={'Телефон'}
                value={params.phone}
                onBlur={() => setErrors('phone')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  changePhoneParam('phone', e.target.value || '')
                }
                showError={phoneError}
                errorMessage={'Некорректный номер телефона'}
              />
            </Line>
          </InputsWrapper>

          <Footer>
            <Button
              mode={'neutral'}
              variant={'ghost'}
              onClick={() => setAddNewThirdPartyModalProperties('show', false)}
            >
              Отмена
            </Button>
            <Button
              disabled={
                !isValidThirdPartyContact ||
                loading.addThirdPartyAccount.pending
              }
              onClick={createNewThirdPartyAccount}
            >
              Сохранить
            </Button>
          </Footer>
        </ModalWrapper>
      </Wrapper>
    </Portal>
  );
});
